'use client'

import Image from 'next/image'
import { CheckCircle2, Clock, Users, Award, Star, Heart, Leaf } from 'lucide-react'
import { RichText } from '@/components/RichText'

type ImageSize = {
  url?: string | null
  filename?: string | null
  width?: number | null
  height?: number | null
}

type UploadedLike = {
  url?: string | null
  filename?: string | null
  sizes?: {
    thumbnail?: ImageSize | null
    card?: ImageSize | null
    large?: ImageSize | null
  } | null
}

type AboutBenefitsBlockProps = {
  blockType: 'aboutBenefits'
  id?: string | null
  label?: string | null
  heading?: string | null
  content?: any
  benefits?: Array<{ text?: string | null }> | null
  stats?: Array<{ icon?: string | null; value?: string | null; label?: string | null }> | null
  image?: UploadedLike | number | null
  floatingCards?: Array<{
    icon?: string | null
    title?: string | null
    subtitle?: string | null
    position?: string | null
  }> | null
}

const iconMap = {
  Clock,
  Users,
  Award,
  Star,
  Heart,
} as const

const floatingIconMap = {
  Leaf,
  CheckCircle2,
  Heart,
  Star,
} as const

export const AboutBenefitsBlockComponent: React.FC<AboutBenefitsBlockProps> = ({
  label,
  heading,
  content,
  benefits,
  stats,
  image,
  floatingCards,
}) => {
  const media = typeof image === 'object' ? (image as UploadedLike) : null
  const largeSize = media?.sizes?.large
  const imageURL = largeSize?.url
    ? largeSize.url
    : largeSize?.filename
      ? `/media/${largeSize.filename}`
      : media?.url
        ? media.url
        : media?.filename
          ? `/media/${media.filename}`
          : '/images/cursussen-hero.jpg'

  return (
    <section
      className="border-t border-border/50 bg-muted/30 py-16 md:py-24"
      aria-label="Over onze cursussen"
    >
      <div className="container">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-24">
          {/* Image with blob shape - Left side */}
          <div className="relative order-2 lg:order-1">
            <div className="group relative mx-auto aspect-[4/5] max-w-md cursor-pointer overflow-visible lg:max-w-none">
              {/* Decorative blob backgrounds */}
              <div className="absolute -inset-4 rounded-[60%_40%_30%_70%/60%_30%_70%_40%] bg-gradient-to-br from-primary/20 via-secondary/10 to-primary/5" />
              <div className="absolute -inset-4 translate-x-4 translate-y-4 rounded-[40%_60%_70%_30%/40%_70%_30%_60%] bg-gradient-to-tr from-secondary/15 to-primary/10" />

              {/* Main image */}
              <div className="relative h-full w-full overflow-hidden rounded-[30%_70%_70%_30%/30%_30%_70%_70%] shadow-2xl transition-all duration-300 ease-out group-hover:scale-[1.02] group-hover:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.35)]">
                <Image
                  src={imageURL}
                  alt="Gezonde maaltijd met verse groenten"
                  fill
                  className="object-cover"
                />
              </div>

              {/* Floating accent cards */}
              {floatingCards?.map((card, index) => {
                const IconComponent = card.icon
                  ? floatingIconMap[card.icon as keyof typeof floatingIconMap]
                  : Leaf
                const isBottomLeft = card.position === 'bottomLeft'
                const isGreen = card.icon === 'CheckCircle2'

                return (
                  <div
                    key={index}
                    className={`absolute rounded-2xl border border-border/60 bg-background p-4 shadow-xl transition-all duration-300 ease-out hover:scale-105 hover:shadow-2xl ${
                      isBottomLeft
                        ? '-bottom-6 -left-6 lg:-left-8'
                        : '-right-4 -top-4 lg:-right-6 lg:-top-6'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`flex h-12 w-12 items-center justify-center rounded-xl ${
                          isGreen ? 'bg-green-500/10' : 'bg-primary/10'
                        }`}
                      >
                        <IconComponent
                          className={`h-6 w-6 ${isGreen ? 'text-green-600' : 'text-primary'}`}
                        />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">{card.title}</p>
                        <p className="text-xs text-muted-foreground">{card.subtitle}</p>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Content - Right side */}
          <div className="space-y-6 order-1 lg:order-2">
            <div className="space-y-3">
              {label && (
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">
                  {label}
                </p>
              )}
              {heading && (
                <h2 className="text-3xl font-bold tracking-tight text-foreground md:text-4xl">
                  {heading}
                </h2>
              )}
            </div>

            {content && (
              <div className="space-y-4 text-muted-foreground">
                <RichText data={content} enableGutter={false} enableProse={false} />
              </div>
            )}

            {/* Benefits list */}
            {benefits && benefits.length > 0 && (
              <ul className="grid gap-3 pt-2 sm:grid-cols-2">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10">
                      <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                    </div>
                    <span className="text-foreground">{benefit.text}</span>
                  </li>
                ))}
              </ul>
            )}

            {/* Stats */}
            {stats && stats.length > 0 && (
              <div className="grid grid-cols-3 gap-4 border-t border-border/50 pt-8">
                {stats.map((stat, index) => {
                  const IconComponent = stat.icon
                    ? iconMap[stat.icon as keyof typeof iconMap]
                    : Clock
                  return (
                    <div
                      key={index}
                      className="group/stat flex flex-col items-center rounded-2xl border border-border/40 bg-background p-4 text-center shadow-sm transition-all duration-300 hover:border-primary/30 hover:shadow-md"
                    >
                      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 transition-transform duration-300 group-hover/stat:scale-110">
                        <IconComponent className="h-6 w-6 text-primary" />
                      </div>
                      <p className="text-xl font-bold text-foreground">{stat.value}</p>
                      <p className="text-xs text-muted-foreground">{stat.label}</p>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
